// Szolgáltatás-katalógus — a SZOLGÁLTATÁSOK lap „Katalógus" FÜLE (`routes/services.tsx` a héj; a fül nem
// önálló route). A KÖZÖS DataTable-re épül (a clients.index / invoices
// a referencia-minta): szerver-oldali (BFF) név-kereső (300 ms debounce) + kategória/státusz-szűrő +
// darabszám-meta a toolbarban, szerver-rendezés (name / planCount / priceRange), végtelen görgetés (100/lap).
// A /service-catalog BFF-végpontról fogyaszt (billing.services SHARED busz-erőforrás + i18n-név feloldás +
// katalógus-csomag-dúsítás: csomag-szám, DISTINCT kategóriák, ár-tartomány domináns devizában; a SHARED-fal az
// Odin-oldalon). Page-gate: `service_catalog.view`.
//
// Legacy-parity (régi CRM „Szolgáltatás-katalógus" belső lista): a legacy 2 fül (Egyszerű / Csomag alapú) helyett
// a struktúra-típus a szolgáltatás-cellában alcímként jelenik meg (nincs külön oldal/fül); a legacy oszlopok 1:1:
//   1. Szolgáltatás  — név (itemTitle, rendezhető) + struktúra-típus alcím + státusz-badge   (legacy „Szolgáltatás")
//   2. Kategóriák    — a csomagok DISTINCT fő-kategóriái badge-ként                            (Atlas-dúsítás)
//   3. Csomagok      — katalógus-csomag-szám (rendezhető), jobbra                              (legacy csomag-bontás sűrítve)
//   4. Ár-tartomány  — a domináns deviza min–max nettó ára (rendezhető), jobbra                (legacy „Árazás" sűrítve)
// A státusz-badge a legacyben a név-oszlopban ült → itt is (a szín emerald/rose → success/muted token).
// KIHAGYVA (a contractból NEM renderelhető): a legacy „Ügyfélszolgálat" oszlop (client_support nincs a contractban),
// a billing_type/pricing_type badge-ek (a plan-adat nincs a sor-szerződésben), és a részletes ártétel-bontás
// (tier/overflow/passzív — a BFF csak a domináns min–max-ot adja). A legacy inline oszlop-keresők + státusz-szöveg-
// hack helyett tiszta név-kereső + kategória/státusz dropdown (a sibling-oldalak mintája). SOR-MŰVELETEK
// (szerkesztés `manage_services` / törlés `delete_services`) MÖGÖTT nincs Atlas-backend (a katalógus írása nincs
// implementálva) → nincs sor-akció (olvasható lista, mint az accounts/invoices).
import { type ServiceCatalogRowSchema } from '@atlas/contracts';
import { PlusIcon } from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { ServiceFormSheet } from '@/components/service-catalog/service-form-sheet';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { IdentityStatusBadge } from '@/components/shared/identity-status-badge';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { DeleteRowAction, EditRowAction, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { Text } from '@/components/ui/text';
import { serviceCatalogKeys } from '@/features/service-catalog/api';
import { priceRowLabel, VISIBLE_PRICE_ROWS } from '@/features/service-catalog/price-rows';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListFilters } from '@/hooks/use-list-filters';
import type { TabView } from '@/lib/tab-view';
import { api } from '@/lib/api';
import { formatPriceSymbol } from '@/lib/format-money';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// Deep-link: `?edit=<id>` → a szerkesztő-sheet a megadott szolgáltatásra nyílik (a command palette „2. rungja").
type ServiceRow = z.infer<typeof ServiceCatalogRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.serviceCatalog.query>>;

export interface ServiceCatalogViewProps {
  /** A fül AKTÍV-e: rejtett fülön a lekérdezés el sem indul (nem terheli az Odin kérés-plafonját). */
  enabled: boolean;
  /** Deep-link: a `?cat_edit=` paraméterből érkező szolgáltatás-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

/**
 * Hány ársor látszik a lista árazás-cellájában csomagonként. A legacy egyet mutatott (a többit legördülőben);
 * három még nem borítja meg a sor-magasságot, de egy alapdíj+sáv+overflow hármast már kiad.
 */

const PAGE_SIZE = 100;
const ALL = '__all__';

// A legacy enum-kulcsok (adat-parity) a keresőhöz/szűrőkhöz. Szűrő-sorrend: szerkezet → fő-kategória →
// alkategória → billing-típus → pricing-típus → státusz.
const STRUCTURE_OPTIONS = ['simple', 'package'];
const CATEGORY_OPTIONS = ['product', 'advisory', 'other'];
const SUBCATEGORY_OPTIONS = [
  'roomsome',
  'innovation',
  'digital_marketing',
  'revenue_management',
  'web_development',
  'other',
];
const BILLING_OPTIONS = ['recurring', 'one_time'];
const PRICING_OPTIONS = ['flat', 'tiered', 'base_tiered', 'commission'];
const STATUS_OPTIONS = ['active', 'inactive'];

export function useServiceCatalogView({
  enabled,
  deepLinkEditId,
  onClearDeepLink,
}: ServiceCatalogViewProps): TabView {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('service_catalog.view');
  // Jog-vezérelt akciók: services.manage → „+ Új" + ceruza (create/edit sheet); services.delete → kuka.
  const canManage = can('services.manage');
  const canDelete = can('services.delete');

  // Szerkesztő-sheet: `null` → zárt; `{ service: null }` → LÉTREHOZÁS; `{ service }` → SZERKESZTÉS.
  const [formTarget, setFormTarget] = useState<{ service: ServiceRow | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ServiceRow | null>(null);
  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    cat_q: '',
    cat_category: ALL,
    cat_status: ALL,
    cat_sort: 'name',
    cat_order: 'asc',
  });
  const q = urlFilters.cat_q;
  const category = urlFilters.cat_category;
  const status = urlFilters.cat_status;
  const sort = urlFilters.cat_sort;
  const order = urlFilters.cat_order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  const [structureType, setStructureType] = useState<string>(ALL);
  const [subCategory, setSubCategory] = useState<string>(ALL);
  const [billingType, setBillingType] = useState<string>(ALL);
  const [pricingType, setPricingType] = useState<string>(ALL);
  // Szerver-rendezés: a `sort` a kulcs, az `order` az irány. Alap: name ASC (a legacy alap-rendezés).

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ cat_q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: service_catalog.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  const filters = {
    q,
    structureType,
    category,
    subCategory,
    billingType,
    pricingType,
    status,
    sort,
    order,
  };
  const listKey = serviceCatalogKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: enabled && !gated,
    queryFn: ({ pageParam }) =>
      api.serviceCatalog.query({
        query: {
          q: q || undefined,
          structureType: structureType === ALL ? undefined : structureType,
          category: category === ALL ? undefined : category,
          subCategory: subCategory === ALL ? undefined : subCategory,
          billingType: billingType === ALL ? undefined : billingType,
          pricingType: pricingType === ALL ? undefined : pricingType,
          status: status === ALL ? undefined : status,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.services : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` szolgáltatásra nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<ServiceRow>({
    id: deepLinkEditId,
    rows,
    getId: (svc) => svc.id,
    onOpen: (svc) => setFormTarget({ service: svc }),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters =
    q !== '' ||
    structureType !== ALL ||
    category !== ALL ||
    subCategory !== ALL ||
    billingType !== ALL ||
    pricingType !== ALL ||
    status !== ALL;

  // Rendezés: azonos oszlopra kattintás → irányváltás; új oszlopra → az adott kulcs ASC.
  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort
          ? { cat_order: order === 'asc' ? 'desc' : 'asc' }
          : { cat_sort: col, cat_order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista lista-sor eltávolítás + rollback): a soft-delete a BFF `/service-catalog/:id` (services.delete).
  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.serviceDelete.mutation({ params: { id } }),
    invalidateKeys: [serviceCatalogKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        if (!old) return old;
        const id = deleteTarget?.id;
        if (!id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    services: p.body.services.filter((s) => s.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:serviceCatalog.deleted'),
      conflict: t('toast:serviceCatalog.conflict'),
      forbidden: t('toast:serviceCatalog.forbidden'),
      validation: t('toast:serviceCatalog.validation'),
      error: t('toast:serviceCatalog.error'),
    },
  });

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
  }

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;

  const columns: DataTableColumn<ServiceRow>[] = [
    {
      // 1. Szolgáltatás — név (rendezhető, itemTitle) + struktúra-típus alcím + státusz-badge (a legacy „Szolgáltatás").
      id: 'name',
      header: t('serviceCatalog.columns.service'),
      sortable: true,
      cardLabel: t('serviceCatalog.columns.service'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      cell: (svc) => (
        <div className="min-w-0 space-y-1.5">
          <Text variant="itemTitle" as="div" className="truncate">
            {svc.name || emDash}
          </Text>
          {/* Szerkezet-típus: a KÖZÖS badge-katalógus `serviceStructure` jele (egyszerű = kocka, brand — mint a termék /
              csomag = rács, violet — mint a tanácsadás), nem puszta szöveg. */}
          <div>
            <DomainBadge
              kind="serviceStructure"
              value={svc.structureType}
              label={t(`serviceCatalog.structureLabel.${svc.structureType}`, {
                defaultValue: svc.structureType,
              })}
            />
          </div>
        </div>
      ),
    },
    {
      // 2. Árazás — a legacy árazás-oszlop 1:1: csomagonként legördülő (ExpandableCell) breakdown —
      // billing-típus badge (ikonnal) + pricing-típus + ársorok (ár + deviza + tier/overflow/passive/minimum részlet).
      id: 'pricing',
      header: t('serviceCatalog.columns.pricing'),
      cardLabel: t('serviceCatalog.columns.pricing'),
      width: 'minmax(0,2.3fr)',
      multiline: true,
      cell: (svc) => {
        if (svc.pricing.length === 0) return emDash;
        // A csomag-nevet MINDIG kiírjuk (simple-nél = a szolgáltatás neve) — így a név-balra / badge-jobbra
        // elrendezés simple-nél is kitöltött (nem üres bal + jobbra lökött badge).
        // A csomagok NYELV-FÜGGŐ ABC-sorrendben (olvashatóság) — locale-tudatos collation a feloldott néven.
        const plans = [...svc.pricing].sort((a, b) => a.name.localeCompare(b.name, i18n.language));
        return (
          <ExpandableCell
            items={plans}
            getKey={(_p, i) => String(i)}
            visible={1}
            threshold={1}
            moreLabel={(n) => t('serviceCatalog.morePlans', { count: n })}
            triggerAriaLabel={t('serviceCatalog.morePlansAria')}
            renderItem={(plan) => {
              const billingLabel = t(`serviceCatalog.billingTypeLabel.${plan.billingType}`, {
                defaultValue: plan.billingType,
              });
              return (
                <div className="min-w-0 space-y-1">
                  {/* Fej-sor: a csomag NEVE BALRA, a badge JOBBRA (justify-between) — a sorokban és a legördülőben is. */}
                  <div className="flex items-center justify-between gap-2">
                    {plan.name ? (
                      // A csomag-név az OSZLOP alap-betűjén (halvány) — NEM `itemTitle`. Ez a MÁSODIK oszlop:
                      // a rendszer-konvenció szerint az erős/kiemelt betű az ELSŐ oszlop sajátja (a
                      // szolgáltatás-neve), a többi oszlop a „Felelős ügyfélszolgálat" oszlop betűjét viszi
                      // (user-döntés — a csomag-név itt túl hangsúlyos volt).
                      <span className="min-w-0 flex-1 truncate">{plan.name}</span>
                    ) : (
                      <span className="min-w-0 flex-1" />
                    )}
                    {/* KÉT jelvény (user-döntés): az ÁRAZÁS a saját ikonjával (fix díjas / sávos / …), mellette a
                        számlázás-típus (ismétlődő / eseti) a sajátjával. Korábban egy jelvény volt: a szöveg az
                        árazás, az ikon és a szín a számlázás-típusé — az ikon mást jelentett, mint a felirat. */}
                    <DomainBadge
                      kind="pricingType"
                      value={plan.pricingType}
                      label={t(`serviceCatalog.pricingTypeLabel.${plan.pricingType}`, {
                        defaultValue: plan.pricingType,
                      })}
                      className="shrink-0"
                    />
                    <DomainBadge
                      kind="billingType"
                      value={plan.billingType}
                      label={billingLabel}
                      className="shrink-0"
                    />
                  </div>
                  {plan.prices.length === 0 ? (
                    <Text variant="secondary" as="div" className="text-xs">
                      {t('serviceCatalog.noPrices')}
                    </Text>
                  ) : (
                    // Ársorok: BAL a mennyiség/típus-címke, JOBB az ár (jobbra igazítva) — a legacy elrendezés 1:1.
                    //
                    // CSONKOLVA (`VISIBLE_PRICE_ROWS`): a legacy is csak az ELSŐ ársort mutatta a cellában, a
                    // többit legördülőben (`$prices->skip(1)`). Nálunk eddig MIND kilistázódott, ezért egy
                    // 11-sávos csomag (Hírlevél) sora ~4× magasabb volt a többinél — a tábla ritmusa elveszett.
                    // Miért statikus „+N" szöveg és nem újabb legördülő: ez a blokk MÁR egy legördülő elemében
                    // is renderelődik (a csomag-váltó `ExpandableCell`-ben), és egymásba ágyazott Radix-menü
                    // rossz fókusz-kezeléshez vezet. A TELJES bontás a szerkesztőben van, egy kattintásra.
                    <div className="space-y-0.5">
                      {plan.prices.slice(0, VISIBLE_PRICE_ROWS).map((pr, i) => (
                        <div key={i} className="flex items-baseline justify-between gap-3">
                          <span className="text-muted-foreground min-w-0 truncate text-xs">
                            {priceRowLabel(pr, t)}
                          </span>
                          <span className="shrink-0 text-sm tabular-nums">
                            {formatPriceSymbol(pr.netPrice, pr.currency, i18n.language)}
                          </span>
                        </div>
                      ))}
                      {plan.prices.length > VISIBLE_PRICE_ROWS && (
                        <Text tone="tertiary" as="div" className="text-xs">
                          {t('serviceCatalog.morePrices', {
                            count: plan.prices.length - VISIBLE_PRICE_ROWS,
                          })}
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              );
            }}
          />
        );
      },
    },
    {
      // 3. Felelős ügyfélszolgálat — a legacy lista HARMADIK oszlopa (`client_support`): melyik ügyfélszolgálat
      //    felel a szolgáltatásért (ez dönti el a jegy-útvonalat is). A név a BFF-ben feloldva (best-effort:
      //    jog/törölt csapat híján null → em-dash). Mérve: 27 szolgáltatásból 20-nak van csapata.
      id: 'supportTeam',
      card: 'subtitle',
      header: t('serviceCatalog.columns.supportTeam'),
      cardLabel: t('serviceCatalog.columns.supportTeam'),
      // A fejléc ezen a listán a leghosszabb („Felelős ügyfélszolgálat") → elég széles, hogy NE csonkoljon
      // (mérve: 1.3fr-en „FELELŐS ÜGYFÉLSZOLG…" lett).
      width: 'minmax(0,1.9fr)',
      multiline: true,
      cell: (svc) =>
        svc.supportTeamName ? <span className="truncate">{svc.supportTeamName}</span> : emDash,
    },
    {
      // 4. Státusz — önálló, dedikált oszlop (a Munkatársak-oldal mintája): közös IdentityStatusBadge
      // (vezető ikon + kisbetűs címke + szín-variáns), NEM az első oszlopba zsúfolva.
      id: 'status',
      header: t('serviceCatalog.columns.status'),
      cardLabel: t('serviceCatalog.columns.status'),
      width: 'minmax(0,0.9fr)',
      // Badge-cella: `multiline` → overflow-hidden (nincs „…" a pill után keskenyítéskor).
      multiline: true,
      cell: (svc) => (
        <IdentityStatusBadge
          status={svc.status}
          label={t(`serviceCatalog.statusLabel.${svc.status}`, { defaultValue: svc.status })}
        />
      ),
    },
  ];

  // A FÜL NÉZETE — a lap-héj (`routes/services.tsx`) rendereli az `AppShell`-be. A mobil FAB elsődleges
  // akciója csak manage-joggal van (desktopon a sáv `ListNewButton`-ja ugyanez).
  return {
    ...(canManage
      ? {
          primaryAction: {
            icon: <PlusIcon className="size-5 stroke-2" />,
            label: t('serviceCatalog.new'),
            onClick: () => setFormTarget({ service: null }),
          },
        }
      : {}),
    toolbar: (
      // KÖZÖS ListToolbar: kereső + „Szűrők (N)" popover (kategória → státusz) + darabszám-meta.
      // Szűrő-sorrend a legacy-parity szerint: kategória → státusz.
      <ListToolbar
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('common.searchPlaceholder'),
        }}
        filters={[
          {
            key: 'structureType',
            label: t('serviceCatalog.filterStructure'),
            value: structureType,
            onChange: setStructureType,
            options: STRUCTURE_OPTIONS.map((v) => ({
              value: v,
              label: t(`serviceCatalog.structureLabel.${v}`, { defaultValue: v }),
            })),
            allValue: ALL,
          },
          {
            key: 'category',
            label: t('serviceCatalog.filterCategory'),
            value: category,
            onChange: (v: string) => setFilters({ cat_category: v }),
            options: CATEGORY_OPTIONS.map((c) => ({
              value: c,
              label: t(`serviceCatalog.categoryLabel.${c}`, { defaultValue: c }),
            })),
            allValue: ALL,
          },
          {
            key: 'subCategory',
            label: t('serviceCatalog.filterSubCategory'),
            value: subCategory,
            onChange: setSubCategory,
            options: SUBCATEGORY_OPTIONS.map((c) => ({
              value: c,
              label: t(`serviceCatalog.subCategoryLabel.${c}`, { defaultValue: c }),
            })),
            allValue: ALL,
          },
          {
            key: 'billingType',
            label: t('serviceCatalog.filterBilling'),
            value: billingType,
            onChange: setBillingType,
            options: BILLING_OPTIONS.map((v) => ({
              value: v,
              label: t(`serviceCatalog.billingTypeLabel.${v}`, { defaultValue: v }),
            })),
            allValue: ALL,
          },
          {
            key: 'pricingType',
            label: t('serviceCatalog.filterPricing'),
            value: pricingType,
            onChange: setPricingType,
            options: PRICING_OPTIONS.map((v) => ({
              value: v,
              label: t(`serviceCatalog.pricingTypeLabel.${v}`, { defaultValue: v }),
            })),
            allValue: ALL,
          },
          {
            key: 'status',
            label: t('serviceCatalog.filterStatus'),
            value: status,
            onChange: (v: string) => setFilters({ cat_status: v }),
            options: STATUS_OPTIONS.map((s) => ({
              value: s,
              label: t(`serviceCatalog.statusLabel.${s}`, { defaultValue: s }),
            })),
            allValue: ALL,
          },
        ]}
        meta={
          infinite.isFetching && !infinite.isFetchingNextPage
            ? t('serviceCatalog.loading')
            : !isError && !infinite.isLoading
              ? t('serviceCatalog.countOf', { shown: rows.length, total })
              : ''
        }
        actions={
          // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
          <div className="hidden md:block">
            <ListNewButton
              label={t('serviceCatalog.new')}
              onClick={() => setFormTarget({ service: null })}
              enabled={canManage}
              disabledTooltip={t('common.noCreatePermission')}
            />
          </div>
        }
      />
    ),
    content: (
      <>
        <div className="flex h-full flex-col px-4 pb-2">
          <DataTable<ServiceRow>
            columns={columns}
            rows={rows}
            getRowId={(svc) => svc.id}
            sort={sort}
            order={order}
            onSort={toggleSort}
            isLoading={!gated && (permsLoading || infinite.isLoading)}
            isError={isError}
            errorState={
              <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
            }
            emptyState={
              hasFilters ? (
                <EmptyState
                  title={t('serviceCatalog.noMatches')}
                  description={t('serviceCatalog.noMatchesDesc')}
                />
              ) : (
                <EmptyState
                  title={t('serviceCatalog.empty')}
                  description={t('serviceCatalog.emptyDesc')}
                />
              )
            }
            hasNextPage={infinite.hasNextPage}
            isFetchingNextPage={infinite.isFetchingNextPage}
            onLoadMore={loadMore}
            rowActionsLabel={t('serviceCatalog.columns.actions')}
            actionsColWidth={
              canManage && canDelete ? '7rem' : canManage || canDelete ? '4rem' : '0'
            }
            rowActions={
              canManage || canDelete
                ? (svc) => (
                    <RowActions>
                      {canManage && (
                        <EditRowAction
                          label={t('serviceCatalog.edit')}
                          onClick={() => setFormTarget({ service: svc })}
                        />
                      )}
                      {canDelete && (
                        <DeleteRowAction
                          label={t('serviceCatalog.delete')}
                          onClick={() => setDeleteTarget(svc)}
                        />
                      )}
                    </RowActions>
                  )
                : undefined
            }
          />
        </div>

        <ServiceFormSheet
          open={formTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setFormTarget(null);
              onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
            }
          }}
          service={formTarget?.service ?? null}
        />

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title={t('serviceCatalog.deleteTitle')}
          description={t('serviceCatalog.deleteDesc', { name: deleteTarget?.name ?? '' })}
          confirmLabel={t('serviceCatalog.delete')}
          cancelLabel={t('serviceCatalog.cancel')}
          pendingLabel={t('serviceCatalog.deleting')}
          destructive
          pending={removeMutation.isPending}
          onConfirm={confirmDelete}
        />
      </>
    ),
  };
}
